/**
 * The one canonical set of call outcomes.
 *
 * Three different sets existed before this: four in the product design, six
 * hard-coded in the mobile sheet, and a fourth set named only in a comment on
 * calls.call_result. None of them were ever written to the database -- the
 * mobile sheet POSTed a Twilio CallSid into a route expecting a numeric lead id,
 * so every save 404'd behind a "Saved" toast. That bug is why this can be
 * adopted outright with no backfill.
 *
 * `isConnect` means a human was reached. `not_interested` counts: reaching
 * someone who says no is still reaching someone, and a connect rate that
 * excluded rejections would flatter every rep who gave up early.
 *
 * Metrics MUST derive from these flags rather than comparing strings at the
 * call site, so that adding an outcome cannot silently change what a rate means.
 */

export interface Disposition {
  readonly value: string;
  readonly label: string;
  /** A human was reached. */
  readonly isConnect: boolean;
  /** Counts toward the "meetings" figure. Implies isConnect. */
  readonly isMeeting: boolean;
  /** Sets calls.follow_up_required. */
  readonly createsFollowUp?: boolean;
  /** The number does not belong to this contact. */
  readonly flagsLead?: boolean;
}

export const DISPOSITIONS = [
  { value: 'connected',       label: 'Connected',       isConnect: true,  isMeeting: false },
  { value: 'callback',        label: 'Follow-up needed', isConnect: true, isMeeting: false, createsFollowUp: true },
  { value: 'appointment_set', label: 'Meeting booked',  isConnect: true,  isMeeting: true },
  { value: 'not_interested',  label: 'Not interested',  isConnect: true,  isMeeting: false },
  { value: 'no_answer',       label: 'No answer',       isConnect: false, isMeeting: false },
  { value: 'left_voicemail',  label: 'Left voicemail',  isConnect: false, isMeeting: false },
  { value: 'wrong_number',    label: 'Wrong number',    isConnect: false, isMeeting: false, flagsLead: true },
  // End-of-call outcome sheet (mobile dialer). Asked only after a call that
  // connected, so all three are connects.
  { value: 'interested',      label: 'Interested',      isConnect: true,  isMeeting: false },
  { value: 'resolved',        label: 'Resolved',        isConnect: true,  isMeeting: false },
  { value: 'other',           label: 'Other',           isConnect: true,  isMeeting: false },
] as const satisfies readonly Disposition[];

/**
 * What the end-of-call "Call outcome" sheet offers for an ANSWERED call, in
 * display order. Same vocabulary (and calls.call_result column) as every other
 * disposition, so reports and dial-session metrics count them without change.
 */
export const ANSWERED_CALL_OUTCOMES = [
  'interested', 'not_interested', 'callback', 'appointment_set', 'wrong_number', 'left_voicemail', 'resolved', 'other',
] as const satisfies readonly DispositionValueOf<typeof DISPOSITIONS>[];

type DispositionValueOf<T extends readonly { value: string }[]> = T[number]['value'];

/**
 * Whether the dialer should ask for an outcome when a call ends: only a call
 * that actually connected (missed / busy / failed / no-answer never do).
 */
export function shouldPromptOutcome(c: { status?: string | null; duration?: number | null; wasConnected?: boolean }): boolean {
  const s = String(c.status || '').toLowerCase();
  if (['missed', 'no-answer', 'no_answer', 'busy', 'failed', 'canceled', 'cancelled', 'rejected'].includes(s)) return false;
  return (c.duration ?? 0) > 0 && c.wasConnected !== false;
}

export type DispositionValue = (typeof DISPOSITIONS)[number]['value'];

const BY_VALUE = new Map<string, Disposition>(DISPOSITIONS.map((d) => [d.value, d]));

export function isValidDisposition(value: string): value is DispositionValue {
  return BY_VALUE.has(value);
}

export function getDisposition(value: DispositionValue): Disposition {
  return BY_VALUE.get(value)!;
}

/**
 * Human-readable label for a value read back from the database.
 *
 * `getDisposition` requires a DispositionValue, which a `varchar` column read
 * cannot promise: `calls.call_result` predates this vocabulary and holds four
 * older ad-hoc values. Rendering the raw value beats rendering "undefined".
 */
export function dispositionLabel(value: string | null | undefined): string {
  if (!value) return '';
  return BY_VALUE.get(value)?.label ?? value;
}

/** A human was reached. Unknown values are not connects. */
export function countsAsConnect(value: string): boolean {
  return BY_VALUE.get(value)?.isConnect ?? false;
}

/** Counts toward "meetings". Unknown values are not meetings. */
export function countsAsMeeting(value: string): boolean {
  return BY_VALUE.get(value)?.isMeeting ?? false;
}
